import React, { useState, useRef } from 'react';
import { Link } from 'react-router';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import authService from '../../services/auth.service';
import { getAuthErrorMessage } from '../../utils/authErrorHandler';
import AuthShell from './AuthShell';
import AuthField from './AuthField';
import AuthSubmitButton from './AuthSubmitButton';

const PasswordReset: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const isSubmittingRef = useRef(false);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    setFieldError('');
    if (!validateEmail(email)) {
      setFieldError(t('auth:passwordReset.emailInvalid'));
      return;
    }
    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      await authService.resetPassword({ email });
      setIsSubmitted(true);
    } catch (error: any) {
      const errorMessage = getAuthErrorMessage(error, t, 'passwordReset');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // ---------- Confirmation state (email sent) ----------
  if (isSubmitted) {
    return (
      <AuthShell title={t('auth:passwordReset.checkEmailTitle')}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <CheckCircleIcon className="h-6 w-6 text-emerald-400" />
          </div>
          <p className="mt-6 text-base text-white/70">
            {t('auth:passwordReset.sentInstructionsTo')}
          </p>
          <p className="mt-2 text-base text-white">{email}</p>
          <p className="mt-4 text-sm text-white/50">
            {t('auth:passwordReset.checkSpamFolder')}
          </p>
          <div className="mt-10 w-full">
            <Link
              to="/signin"
              className="block w-full inline-flex items-center justify-center px-5 py-3 text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {t('auth:passwordReset.backToSignIn')}
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  // ---------- Form state ----------
  return (
    <AuthShell
      title={t('auth:passwordReset.title')}
      subtitle={t('auth:passwordReset.subtitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthField
          id="email"
          name="email"
          type="email"
          label={t('auth:passwordReset.emailLabel')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldError('');
          }}
          placeholder={t('auth:passwordReset.emailPlaceholder')}
          error={fieldError}
          icon={EnvelopeIcon}
          required
          autoComplete="email"
        />

        <AuthSubmitButton
          isLoading={isLoading}
          loadingLabel={t('auth:passwordReset.submittingButton')}
        >
          {t('auth:passwordReset.submitButton')}
        </AuthSubmitButton>
      </form>

      <div className="mt-8 flex items-center gap-3 text-sm">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-white/40 uppercase tracking-[0.2em] text-xs">
          {t('auth:passwordReset.or')}
        </span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <p className="mt-8 text-center text-sm text-white/60">
        {t('auth:passwordReset.rememberPassword')}{' '}
        <Link
          to="/signin"
          className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black font-medium"
        >
          {t('auth:passwordReset.backToSignIn')}
        </Link>
      </p>
    </AuthShell>
  );
};

export default PasswordReset;
